import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { DxPopoverModule } from 'devextreme-angular';
import { AppGlobals } from 'src/app/app.globals';
import {
  NOTIFICATION_CATEGORY_LABELS,
  NotificationCategory,
  NotificationCenterService,
  NotificationItem,
} from 'src/app/services/notification-center.service';
import { GpdCheckboxComponent } from '../gpd-checkbox/gpd-checkbox.component';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { notificationText } from './notification-titles';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';

// Dates and relative times are pinned to German rather than left to the browser's locale: the rest
// of the app formats this way, and a reader on an English browser would otherwise see 9/17/2026
// next to 17.09.2026 everywhere else.
const NOTIFICATION_LOCALE = 'de-DE';

interface NotificationRow extends NotificationItem {
  title: string;
  description: string;
}

interface DayGroup {
  label: string;
  rows: NotificationRow[];
}

@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, DxPopoverModule, SvgIconComponent, GpdCheckboxComponent, UserAvatarComponent],
})
export class NotificationBellComponent implements OnInit {
  private static readonly FilterStorageKey = 'notificationCenter.categories';
  private readonly pageSize = 20;

  readonly open = signal(false);
  readonly loading = signal(false);
  readonly unreadOnly = signal(true);
  readonly rows = signal<NotificationRow[]>([]);
  readonly totalCount = signal(0);
  readonly selectedCategories = signal<NotificationCategory[]>([]);
  readonly loadError = signal('');
  readonly filterOpen = signal(false);

  readonly hasMore = computed(() => this.rows().length < this.totalCount());
  readonly groups = computed(() => this.groupByDay(this.rows()));

  readonly categories = Object.entries(NOTIFICATION_CATEGORY_LABELS).map(([key, label]) => ({
    key: key as NotificationCategory,
    label,
  }));

  constructor(
    public notifications: NotificationCenterService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.restoreFilter();
    this.notifications.refreshUnreadCount().catch(() => undefined);
  }

  togglePanel(): void {
    const next = !this.open();
    this.open.set(next);

    if (next) void this.load();
  }

  closePanel(): void {
    this.open.set(false);
  }

  setUnreadOnly(value: boolean): void {
    if (this.unreadOnly() === value) return;

    this.unreadOnly.set(value);
    void this.load();
  }

  toggleCategory(category: NotificationCategory): void {
    const selected = this.selectedCategories();
    const next = selected.includes(category) ? selected.filter((c) => c !== category) : [...selected, category];

    this.selectedCategories.set(next);
    this.persistFilter(next);
    void this.load();
  }

  isCategorySelected(category: NotificationCategory): boolean {
    return this.selectedCategories().includes(category);
  }

  clearCategories(): void {
    this.selectedCategories.set([]);
    this.persistFilter([]);
    void this.load();
  }

  async load(append = false): Promise<void> {
    this.loading.set(true);
    this.loadError.set('');

    try {
      const pageNumber = append ? Math.floor(this.rows().length / this.pageSize) + 1 : 1;

      const result = await this.notifications.getNotifications({
        pageNumber,
        pageSize: this.pageSize,
        unreadOnly: this.unreadOnly(),
        categories: this.selectedCategories(),
      });

      const rows = (result.items ?? []).map((item) => this.toRow(item));

      this.rows.set(append ? [...this.rows(), ...rows] : rows);
      this.totalCount.set(result.totalCount ?? 0);
    } catch (error: any) {
      this.loadError.set(error?.message || $localize`:@@notificationLoadFailed:Notifications could not be loaded.`);
    } finally {
      this.loading.set(false);
    }
  }

  loadMore(): void {
    void this.load(true);
  }

  async markAllRead(): Promise<void> {
    try {
      await this.notifications.markAllRead();
      await this.load();
    } catch (error: any) {
      AppGlobals.ShowErrorMessage(error?.message);
    }
  }

  async openNotification(row: NotificationRow): Promise<void> {
    if (!row.isRead) {
      // Read state is worth losing if this fails; the navigation is not.
      try {
        await this.notifications.markRead([row.id]);
        this.rows.set(this.rows().map((r) => (r.id === row.id ? { ...r, isRead: true } : r)));
      } catch {
        // ignored - the panel refreshes on next open
      }
    }

    this.closePanel();

    if (!row.deepLink || this.isReport(row)) return;

    const internalPath = this.toInternalPath(row.deepLink);

    if (internalPath) {
      void this.router.navigateByUrl(internalPath);
    } else {
      window.open(row.deepLink, '_blank', 'noopener');
    }
  }

  // Mirrors the icon the design gives each category row in the filter.
  private static readonly CategoryIcons: Record<string, string> = {
    Projects: 'folder',
    Tasks: 'check-list',
    ProjectHub: 'comment',
    ContractsAndDeadlines: 'contracts',
    Costs: 'coins-euro',
    GeneralUpdates: 'bell',
  };

  categoryIcon(category: NotificationCategory): string {
    return NotificationBellComponent.CategoryIcons[category] ?? 'folder';
  }

  toggleFilterMenu(): void {
    this.filterOpen.set(!this.filterOpen());
  }

  openSettings(): void {
    this.closePanel();
    void this.router.navigateByUrl('/notification-settings');
  }

  // Reports, database exports and deadline reminders are raised by a job, not a person, so they
  // carry no actor at all - an avatar on those rows would stand for nobody.
  isSystem(row: NotificationRow): boolean {
    return !this.actorLabel(row);
  }

  actorLabel(row: NotificationRow): string {
    return (row.actorName || row.actorEmail || '').trim();
  }

  // No avatar image reaches the panel - the API returns the actor's email and, when Heimdall
  // resolves it, a display name. Initials keep the design's avatar column meaningful.
  initials(row: NotificationRow): string {
    const source = this.actorLabel(row);

    if (!source) return '';

    const parts = source.split(/[\s.@_-]+/).filter(Boolean);
    const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : source.slice(0, 2);

    return letters.toUpperCase();
  }

  // There is no page in the app that shows a report, so a report row never links anywhere - not
  // even the rows raised before that was settled, which still carry a stored deep link.
  isReport(row: NotificationRow): boolean {
    return row.type === 'WeeklySummary' || row.type === 'MonthlySummary';
  }

  // The row's call to action names where the link goes, so it reads as "To task" rather than a bare
  // URL. Anything without a deep link gets no action at all.
  actionLabel(row: NotificationRow): string {
    if (!row.deepLink || this.isReport(row)) return '';

    if (row.type === 'CostOverview') return $localize`:@@notificationActionToCostOverview:To cost overview`;

    switch (row.entityType) {
      case 'PROJECT_TASK':
        return row.deepLink.endsWith('/site-overview')
          ? $localize`:@@notificationActionToSite:To site`
          : $localize`:@@notificationActionToTask:To task`;
      case 'CONTRACT':
        return $localize`:@@notificationActionToContract:To contract`;
      case 'PROJECT_COST':
        return $localize`:@@notificationActionToCosts:To costs`;
      case 'SPARK':
        return $localize`:@@notificationActionToSpark:To Spark`;
      case 'CONFIG_DATABASE':
        return $localize`:@@notificationActionToExport:To export`;
      case 'PROJECT':
        return $localize`:@@notificationActionToProject:To project`;
      default:
        return $localize`:@@notificationActionOpen:Open`;
    }
  }

  relativeTime(occurredAt: string): string {
    const seconds = Math.round((Date.now() - new Date(occurredAt).getTime()) / 1000);

    // Clock skew between the API and the browser must not read as "in 3 minutes".
    if (seconds < 60) return $localize`:@@notificationJustNow:just now`;

    const format = new Intl.RelativeTimeFormat(NOTIFICATION_LOCALE, { numeric: 'auto' });
    const steps: [Intl.RelativeTimeFormatUnit, number][] = [
      ['minute', 60],
      ['hour', 3600],
      ['day', 86400],
      ['week', 604800],
      ['month', 2592000],
      ['year', 31536000],
    ];

    let chosen: [Intl.RelativeTimeFormatUnit, number] = steps[0];

    for (const step of steps) {
      if (seconds >= step[1]) chosen = step;
    }

    return format.format(-Math.floor(seconds / chosen[1]), chosen[0]);
  }

  /**
   * The API builds DeepLink against the tenant's UiDomain, so a link into this app must be routed
   * rather than reloaded - anything pointing elsewhere opens in a new tab.
   */
  private toInternalPath(deepLink: string): string | null {
    try {
      const url = new URL(deepLink, window.location.origin);
      return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null;
    } catch {
      return deepLink.startsWith('/') ? deepLink : null;
    }
  }

  private toRow(item: NotificationItem): NotificationRow {
    return { ...item, ...notificationText(item) };
  }

  private groupByDay(rows: NotificationRow[]): DayGroup[] {
    const groups: DayGroup[] = [];

    for (const row of rows) {
      const label = this.dayLabel(new Date(row.occurredAt));
      const existing = groups.find((g) => g.label === label);

      if (existing) existing.rows.push(row);
      else groups.push({ label, rows: [row] });
    }

    return groups;
  }

  private dayLabel(date: Date): string {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (this.isSameDay(date, today)) return $localize`:@@notificationDayToday:Today`;
    if (this.isSameDay(date, yesterday)) return $localize`:@@notificationDayYesterday:Yesterday`;

    return date.toLocaleDateString(NOTIFICATION_LOCALE);
  }

  private isSameDay(a: Date, b: Date): boolean {
    return (
      a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
    );
  }

  private restoreFilter(): void {
    try {
      const stored = window.localStorage.getItem(NotificationBellComponent.FilterStorageKey);
      if (stored) this.selectedCategories.set(JSON.parse(stored));
    } catch {
      // A blocked or corrupt store just means "no filter", never a broken panel.
    }
  }

  private persistFilter(categories: NotificationCategory[]): void {
    try {
      window.localStorage.setItem(NotificationBellComponent.FilterStorageKey, JSON.stringify(categories));
    } catch {
      // ignored
    }
  }
}
